import { SmartDatePicker } from 'app/components/shared/FormComponents/SmartDatePicker'
import { SmartInput } from 'app/components/shared/FormComponents/SmartInput'
import { FormGroup } from 'app/components/shared/FormGroup'
import { FormSection } from 'app/components/shared/FormSection'
import { Grid } from 'app/components/ui/Grid'
import { useLocationFields } from 'app/hooks/useLocationFields'
import { Location } from 'EstraSponsor'
import React from 'react'
import { useFormContext } from 'react-hook-form'

type Props = {
  locations: Location[]
  holderWho:
    | 'holder_address'
    | 'technical_director_address'
    | 'technical_manager_address'
}

export const HolderFormSection = ({
  locations,
  holderWho,
}: Props): JSX.Element => {
  const { watch, setValue, getValues } = useFormContext()

  const provinceIdWatcher = watch(`${holderWho}.province_id`)

  const { provinceOptions, cityOptions } = useLocationFields({
    locations,
    selectedProvinceId: provinceIdWatcher,
    provincesOnly: true,
  })

  const makeHolderFieldName = (fieldName: string) => `${holderWho}.${fieldName}`

  // when holder_address data changes, update primary bank details data with the ones in this older
  // only if primary bank details are empty
  const updatePrimaryBankDetailsData = () => {
    if (holderWho !== 'holder_address') return

    if (getValues('bank_account_1.delegated_fiscal_code') === '') {
      setValue(
        'bank_account_1.delegated_fiscal_code',
        getValues(`${holderWho}.fiscal_code`)
      )
    }

    if (getValues('bank_account_1.delegated_name') === '') {
      setValue('bank_account_1.delegated_name', getValues(`${holderWho}.name`))
    }

    if (getValues('bank_account_1.delegated_surname') === '') {
      setValue(
        'bank_account_1.delegated_surname',
        getValues(`${holderWho}.surname`)
      )
    }

    if (getValues('bank_account_1.delegated_role') === '') {
      setValue('bank_account_1.delegated_role', 'delegato')
    }
  }

  return (
    <FormSection>
      <Grid cols={[6, 6]}>
        <FormGroup>
          <SmartInput
            label='Nome'
            name={makeHolderFieldName('name')}
            onBlur={updatePrimaryBankDetailsData}
            labelRequired
          />
        </FormGroup>
        <FormGroup>
          <SmartInput
            label='Cognome'
            onBlur={updatePrimaryBankDetailsData}
            name={makeHolderFieldName('surname')}
            labelRequired
          />
        </FormGroup>
      </Grid>
      <Grid cols={[4, 4, 4]}>
        <FormGroup>
          <SmartInput
            label='Codice fiscale'
            onBlur={updatePrimaryBankDetailsData}
            name={makeHolderFieldName('fiscal_code')}
            labelRequired
          />
        </FormGroup>
        <FormGroup>
          <SmartDatePicker
            label='Data di nascita'
            name={makeHolderFieldName('birth_date')}
            labelRequired
          />
        </FormGroup>
        <FormGroup>
          <SmartInput
            label='Comune di nascita'
            name={makeHolderFieldName('birth_place')}
            labelRequired
          />
        </FormGroup>
      </Grid>
      <Grid cols={[4, 4, 4]}>
        <FormGroup>
          {' '}
          <SmartInput
            label='Indirizzo di residenza'
            name={makeHolderFieldName('address_street')}
            labelRequired
          />
        </FormGroup>
        <FormGroup>
          <SmartInput
            label='Civico'
            name={makeHolderFieldName('address_number')}
            labelRequired
          />
        </FormGroup>
        <FormGroup>
          <SmartInput
            label='CAP'
            name={makeHolderFieldName('postal_code')}
            labelRequired
          />
        </FormGroup>
      </Grid>
      <Grid cols={[6, 6]}>
        <FormGroup>
          <SmartInput
            type='select'
            label='Provincia'
            name={makeHolderFieldName('province_id')}
            labelRequired
            selectCaretSize='small'
          >
            <option value={''}>Seleziona</option>
            {provinceOptions.map((province) => (
              <option key={province.value} value={province.value}>
                {province.label}
              </option>
            ))}
          </SmartInput>
        </FormGroup>

        <FormGroup>
          <SmartInput
            type='select'
            label='Comune'
            name={makeHolderFieldName('city_id')}
            labelRequired
            selectCaretSize='small'
            disabled={!cityOptions.length}
          >
            <option value={''}>Seleziona</option>
            {cityOptions.map((city) => (
              <option key={city.value} value={city.value}>
                {city.label}
              </option>
            ))}
          </SmartInput>
        </FormGroup>
      </Grid>
    </FormSection>
  )
}
